import React, { useEffect, useMemo, useState } from 'react';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { useOnboarding } from '../../contexts/OnboardingContext';
import './Onboarding.css';

// Guidance for each step lives here (not scattered across screens). The
// active step is the first incomplete one, so the panel follows real
// progress — including modal states like the generation settings window.
const PANEL_STEPS = [
    {
        key: 'foundStory',
        label: 'Find a story',
        explanation: 'Start simple. Pick a time range, category or keyword, then open a story that interests you by clicking on the headline or Next button.',
    },
    {
        key: 'selectedSources',
        label: 'Review sources',
        explanation: 'Review the news sources. The draft will use only the articles you selected. You can unselect sources. Press Next when you are done, or Draft to proceed.',
    },
    {
        key: 'reviewedClaims',
        label: 'Review claims',
        explanation: 'Claims are the facts extracted from your sources. Uncheck anything you do not want, then press Generate draft.',
    },
    {
        key: 'generatedDraft',
        label: 'Generate your draft',
        explanation: 'The recommended settings are ready to use. Press Generate draft to create your first draft.',
    },
    {
        key: 'sawDraft',
        label: 'Visit draft page',
        explanation: 'Press Go to my draft — the drafts page is where every draft you create lives.',
    },
];

const CONFETTI_COLORS = ['#FF6B35', '#2196F3', '#00AEEF', '#9C27B0', '#009688', '#FF9800'];
const CONFETTI_PIECES = Array.from({ length: 18 }, (_, i) => ({
    left: `${(i * 53) % 100}%`,
    background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    animationDelay: `${(i % 6) * 0.25}s`,
    animationDuration: `${2 + ((i * 7) % 10) / 8}s`,
}));

// Bottom-left guidance panel: numbered step list on the left, explanation on
// the right with prev/next browsing so the whole guide can be read upfront.
// Sits above modal overlays; ends with a Finish button and confetti.
function OnboardingPanel() {
    const { enabled, state, panelVisible, isDone, videoUrl, setChecklistDismissed, finishOnboarding } = useOnboarding();

    const activeIndex = useMemo(() => {
        const idx = PANEL_STEPS.findIndex((item) => !state?.steps?.[item.key]);
        return idx === -1 ? PANEL_STEPS.length - 1 : idx;
    }, [state]);

    // Reading mode: browse explanations freely; snaps back to the live step
    // whenever real progress advances.
    const [browseIndex, setBrowseIndex] = useState(null);
    useEffect(() => { setBrowseIndex(null); }, [activeIndex]);

    if (!enabled || !state?.welcomeSeenAt) return null;

    const doneCount = PANEL_STEPS.filter((item) => state.steps?.[item.key]).length;
    const allStepsDone = doneCount === PANEL_STEPS.length;
    const shownIndex = browseIndex ?? activeIndex;
    const shownStep = PANEL_STEPS[shownIndex];

    return (
        <div className="onb-panel-wrap">
            {panelVisible ? (
                <div className="onb-checklist-panel">
                    {isDone && (
                        <div className="onb-confetti" aria-hidden="true">
                            {CONFETTI_PIECES.map((style, i) => (
                                <span key={i} className="onb-confetti-piece" style={style} />
                            ))}
                        </div>
                    )}
                    <div className="onb-checklist-header">
                        <span>{isDone ? 'Congratulations' : 'Create your first draft'}</span>
                        <button
                            type="button"
                            className="onb-checklist-close"
                            onClick={() => setChecklistDismissed(true)}
                            title="Hide guide"
                        >
                            <CloseOutlinedIcon style={{ fontSize: 16 }} />
                        </button>
                    </div>
                    <div className="onb-panel-body">
                        <div className="onb-panel-steps">
                            {PANEL_STEPS.map((item, index) => {
                                const done = isDone || Boolean(state.steps?.[item.key]);
                                const current = !isDone && !allStepsDone && index === activeIndex;
                                return (
                                    <button
                                        type="button"
                                        key={item.key}
                                        className={`onb-checklist-item${done ? ' done' : ''}${current ? ' current' : ''}`}
                                        onClick={() => setBrowseIndex(index)}
                                        title="Read this step"
                                    >
                                        <span className={`onb-step-num${done ? ' done' : ''}${current ? ' current' : ''}`}>
                                            {index + 1}
                                        </span>
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="onb-panel-divider" />
                        <div className="onb-panel-explanation">
                            {isDone ? (
                                <div className="onb-done-message">
                                    You made your first draft!
                                    <br />
                                    You are amazing!
                                </div>
                            ) : allStepsDone ? (
                                <>
                                    <div className="onb-panel-explanation-title">All steps complete</div>
                                    <div className="onb-panel-explanation-text">
                                        Review the draft, make your own changes inside and press Copy draft
                                        at the bottom when finished. You can reach this page anytime from the
                                        user menu at the top right, or click the Alchemiq logo to get back
                                        to the stories.
                                    </div>
                                    <button
                                        type="button"
                                        className="onb-finish-button"
                                        onClick={finishOnboarding}
                                    >
                                        Finish
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="onb-panel-explanation-title">{shownStep.label}</div>
                                    <div className="onb-panel-explanation-text">{shownStep.explanation}</div>
                                    {videoUrl && (
                                        <a
                                            className="onb-panel-video-link"
                                            href={videoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Watch the 2-minute video
                                        </a>
                                    )}
                                    <div className="onb-browse-nav">
                                        <button
                                            type="button"
                                            className="onb-browse-arrow"
                                            onClick={() => setBrowseIndex(Math.max(0, shownIndex - 1))}
                                            disabled={shownIndex === 0}
                                            title="Previous step"
                                        >
                                            <ChevronLeftOutlinedIcon style={{ fontSize: 18 }} />
                                        </button>
                                        <span className="onb-browse-count">Step {shownIndex + 1} of {PANEL_STEPS.length}</span>
                                        <button
                                            type="button"
                                            className="onb-browse-arrow"
                                            onClick={() => setBrowseIndex(Math.min(PANEL_STEPS.length - 1, shownIndex + 1))}
                                            disabled={shownIndex === PANEL_STEPS.length - 1}
                                            title="Next step"
                                        >
                                            <ChevronRightOutlinedIcon style={{ fontSize: 18 }} />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="onb-progress-track">
                        <div
                            className="onb-progress-fill"
                            style={{ width: `${(isDone ? PANEL_STEPS.length : doneCount) / PANEL_STEPS.length * 100}%` }}
                        />
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    className="onb-help-button"
                    onClick={() => setChecklistDismissed(false)}
                    title="Show first-draft guide"
                >
                    <HelpOutlineOutlinedIcon style={{ fontSize: 22 }} />
                </button>
            )}
        </div>
    );
}

export default OnboardingPanel;
